import {cn, cva} from 'platform/shared';

import {useEffect, useRef, useState} from 'react';

import {RequiredTestIdProps, suffixTestId} from 'shared/web';

import './TimedProgressButton.css';

type ButtonVariant = 'success';

export interface TimedProgressButtonProps extends RequiredTestIdProps {
  label: string;
  variant: ButtonVariant;
  durationInMs?: number;
  onClick?: () => void;
  onProgressEnd?: () => void;
}

const timedProgressButtonVariants = cva(
  "relative inline-flex min-w-full select-none items-center justify-center whitespace-nowrap rounded-md px-4 py-2 !text-[length:var(--text-size-small)] font-[var(--font-weight-regular)] leading-[var(--text-leading-small)] tracking-[0] transition-all duration-[250ms] after:absolute after:left-0 after:content-[''] after:top-0 after:h-full after:animate-[timedProgressButtonProgress_linear_forwards] after:bg-[var(--color-severity-success)] after:[animation-duration:inherit] focus-visible:outline-none hover:after:bg-[var(--color-palettes-green-80-100)] hover:after:[animation-play-state:paused]",
  {
    variants: {
      variant: {
        success:
          'bg-[var(--color-palettes-green-80-100)] text-[var(--color-white)] hover:bg-[var(--color-palettes-green-80-100)]',
      },
    },
  }
);

export function TimedProgressButton(props: TimedProgressButtonProps) {
  const durationInMs = props.durationInMs ?? 2000;
  const [isHovered, setIsHovered] = useState(false);
  const timerRef = useRef<NodeJS.Timeout>(null);
  const startTimeRef = useRef<number>(null);
  const remainingTimeRef = useRef<number>(durationInMs);

  const {onProgressEnd} = props;
  useEffect(
    function pauseOrResumeTimerOnHover() {
      if (!isHovered && remainingTimeRef.current > 0) {
        startTimeRef.current = Date.now();
        timerRef.current = setTimeout(() => {
          onProgressEnd?.();
          remainingTimeRef.current = 0;
        }, remainingTimeRef.current);
      } else {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          if (startTimeRef.current) {
            const elapsed = Date.now() - startTimeRef.current;
            remainingTimeRef.current = Math.max(remainingTimeRef.current - elapsed, 0);
          }
        }
      }

      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    },
    [isHovered, durationInMs, onProgressEnd]
  );

  useEffect(
    function resetRemainingTime() {
      remainingTimeRef.current = durationInMs;
    },
    [durationInMs]
  );

  return (
    <button
      className={cn(timedProgressButtonVariants({variant: props.variant}))}
      style={{animationDuration: `${durationInMs}ms`}}
      data-testid={suffixTestId('timedProgressButton', props)}
      onClick={props.onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <span className="relative z-[var(--z-content)]">{props.label}</span>
    </button>
  );
}
